import type { AnyRouteMatch } from "@tanstack/router-core";
import type { JSX } from "react";

import { localizePath } from "@/lib/locale";
import { baseLocale, getLocale, getUrlOrigin, locales, type Locale } from "@/paraglide/runtime.js";
import { OG_LOCALES } from "@complete-athlete/enums";

export const SITE_NAME = "Complete Athlete";

/** Past about this many characters a search result cuts the title off. */
const MAX_TITLE_LENGTH = 65;

/** What a route's `head` may return, as the router renders it into `<head>`. */
export type HeadMeta = JSX.IntrinsicElements["meta"];
export type HeadLink = JSX.IntrinsicElements["link"];
export type HeadScript = JSX.IntrinsicElements["script"];

declare module "@tanstack/router-core" {
  interface StaticDataRouteOption {
    /**
     * The page sets its own `og:image` (a business's cover photo), so the root leaves out the
     * platform's preview image and the fixed size it declares for it.
     */
    ownSocialImage?: boolean;
    /**
     * The page names its own canonical address and language alternates - a blog post, whose
     * languages are separate slugs rather than one path under each prefix (ADR 0025) - so the root
     * leaves both out instead of adding a second, conflicting set.
     */
    ownAlternates?: boolean;
  }
}

/**
 * A page's title with the platform's name after it. A title long enough on its own stays that way,
 * so a search result does not cut off the end of the words people came for.
 */
export const brandedTitle = (title: string): string => {
  const branded = `${title} · ${SITE_NAME}`;
  return branded.length > MAX_TITLE_LENGTH ? title : branded;
};

/**
 * A page's own title and description. Its `head` returns this and the root route fills in the
 * rest; for each meta name the deepest route wins, so these replace the platform's defaults.
 * A business's page passes `brand: false` - its name is the title, not ours (ADR 0016).
 */
export const pageMeta = ({
  title,
  description,
  brand = true,
}: {
  title: string;
  description?: string;
  brand?: boolean;
}): HeadMeta[] => [
  { title: brand ? brandedTitle(title) : title },
  { property: "og:title", content: title },
  ...(description
    ? [
        { name: "description", content: description },
        { property: "og:description", content: description },
      ]
    : []),
];

/**
 * Structured data, as a head script. `<` is escaped so text a business typed (`</script>` in its
 * name) cannot end the script early.
 */
export const jsonLdScript = (data: Record<string, unknown>): HeadScript => ({
  type: "application/ld+json",
  children: JSON.stringify(data).replace(/</g, "\\u003c"),
});

/**
 * The platform as an organization, for a home page's structured data: who runs the site and the
 * logo that stands for it in search results.
 */
export const organizationJsonLd = (): HeadScript => {
  const origin = getUrlOrigin();
  return jsonLdScript({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: `${origin}/`,
    logo: `${origin}/assets/android-chrome-512x512.png`,
  });
};

/** The site itself, which is what lets search results show its name rather than its address. */
export const websiteJsonLd = (): HeadScript =>
  jsonLdScript({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: `${getUrlOrigin()}/`,
  });

/** Where a page sits on the site, from the top down. */
export const breadcrumbJsonLd = (crumbs: Array<{ name: string; url: string }>): HeadScript =>
  jsonLdScript({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  });

/** Whether the root should describe the platform's own preview image for this page. */
export const usesPlatformImage = (matches: AnyRouteMatch[]): boolean =>
  !matches.some((match) => match.staticData?.ownSocialImage);

/** Kept out of search: the page, or a layout above it, set `staticData.sitemap: false`. */
const isHiddenFromSearch = (matches: AnyRouteMatch[]): boolean =>
  matches.some((match) => match.staticData?.sitemap === false);

/**
 * What tells a crawler which address a page is and where its other languages live, built once
 * in the root route from the page being shown. A page kept out of search gets `noindex` and no
 * address at all, so a signed-in screen never names itself canonical.
 *
 * The canonical URL is the page in its own language without the query, so `?redirect=` and
 * friends never count as pages of their own. Every language links every other with hreflang,
 * and x-default is the English page at the plain URL, as on Gravl (ADR 0026).
 */
export const searchHead = (matches: AnyRouteMatch[]): { meta: HeadMeta[]; links: HeadLink[] } => {
  const leaf = matches.at(-1);
  if (!leaf || isHiddenFromSearch(matches)) {
    return { meta: [{ name: "robots", content: "noindex" }], links: [] };
  }

  const origin = getUrlOrigin();
  const locale = getLocale();
  const ownAlternates = matches.some((match) => match.staticData?.ownAlternates);
  // An index route's path ends in a slash (`/pricing/`); the address it answers at does not.
  const path = leaf.pathname.length > 1 ? leaf.pathname.replace(/\/$/, "") : leaf.pathname;
  const urlIn = (target: Locale) => `${origin}${localizePath(path, target)}`;

  return {
    meta: [
      { property: "og:url", content: urlIn(locale) },
      { property: "og:locale", content: OG_LOCALES[locale] },
      ...locales
        .filter((other) => other !== locale)
        .map((other) => ({ property: "og:locale:alternate", content: OG_LOCALES[other] })),
    ],
    links: ownAlternates
      ? []
      : [
          { rel: "canonical", href: urlIn(locale) },
          ...locales.map((target) => ({ rel: "alternate", hrefLang: target, href: urlIn(target) })),
          { rel: "alternate", hrefLang: "x-default", href: urlIn(baseLocale) },
        ],
  };
};
